/**
 * The FOMO card's surface — Figma «CurvedShape Frame» (Crownstudent-MCP
 * 90:27004): a rounded card whose TOP EDGE IS TILTED (the left top corner sits
 * ~6.3px lower than the right one), drawn as a path so the corners stay round.
 *
 * The Figma path is authored for a 400×237 card inside a 440×288.5 box with
 * 20px / 27.5px shadow margins; here it is translated into the card's own
 * box (origin = the card's top-left). `fomoSurfacePath(width, height)` keeps
 * the LEFT side and the tilt as drawn and moves the right edge / bottom edge
 * with the size, so a taller (longer labels) or narrower (mobile 376px) card
 * keeps circular corners — stretching the Figma SVG would flatten them.
 *
 * Used twice by FomoCard: as the frosted layer's `clip-path: path()` and as
 * the inner hairline's `<path d>`.
 */
export const FOMO_SURFACE = { width: 400, height: 237 };

// Right / bottom edges are expressed as distances from the far edge so they
// follow the size; the left column and the tilt line are the Figma numbers.
const R = 24; // bottom corner radius (and the right / bottom bezier reach)
const K = 10.745; // bezier handle = R × (1 − 0.5523)
// Figma 90:27004 (2026-10-27 restyle): 400×236.57 card, 20 / 27.5px margins
const TOP_RIGHT = { v: 24.5, c1y: 11.078, c2x: 11.007, c2y: 0.265, ex: 24.427, ey: 0.504 };
const TOP_LEFT = { lx: 23.573, ly: 6.77, c1x: 10.487, c1y: 7.003, c2y: 17.678, ey: 30.766 };

const f = (n) => String(Math.round(n * 1000) / 1000);

/** The size the path is drawn for — a missing / zero measurement (not laid out yet, Jest) = the Figma card */
export const resolveSurfaceSize = (width, height) => ({
  width: Number.isFinite(width) && width > 0 ? width : FOMO_SURFACE.width,
  height: Number.isFinite(height) && height > 0 ? height : FOMO_SURFACE.height,
});

export const fomoSurfacePath = (width, height) => {
  const { width: w, height: h } = resolveSurfaceSize(width, height);
  return [
    `M${f(R)} ${f(h)}`,
    `H${f(w - R)}`,
    `C${f(w - K)} ${f(h)} ${f(w)} ${f(h - K)} ${f(w)} ${f(h - R)}`,
    `V${f(TOP_RIGHT.v)}`,
    `C${f(w)} ${f(TOP_RIGHT.c1y)} ${f(w - TOP_RIGHT.c2x)} ${f(TOP_RIGHT.c2y)} ${f(w - TOP_RIGHT.ex)} ${f(TOP_RIGHT.ey)}`,
    `L${f(TOP_LEFT.lx)} ${f(TOP_LEFT.ly)}`,
    `C${f(TOP_LEFT.c1x)} ${f(TOP_LEFT.c1y)} 0 ${f(TOP_LEFT.c2y)} 0 ${f(TOP_LEFT.ey)}`,
    `V${f(h - R)}`,
    `C0 ${f(h - K)} ${f(K)} ${f(h)} ${f(R)} ${f(h)}`,
    'Z',
  ].join(' ');
};
